import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} CourseFormRefs
 * @property {HTMLElement} participants - Holds one group of fields per participant.
 * @property {HTMLTemplateElement} template - Markup for one participant, `__i__` marks the participant number.
 */

/**
 * Shows one group of participant fields per ticket in the quantity selector.
 * The fields post to the product form as line item properties ("Name", or "Name 2" when there are several).
 * @extends Component<CourseFormRefs>
 */
class CourseFormComponent extends Component {
  requiredRefs = ['participants', 'template'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#render);
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#render);
  }

  /**
   * Writes the checked allergens into the fieldset's hidden property input, e.g. "Soja, Fruktose".
   * @param {Event} event - The change event, its target is the allergens fieldset.
   */
  updateAllergens({ target }) {
    if (!(target instanceof HTMLFieldSetElement)) return;

    const checked = target.querySelectorAll('input[type="checkbox"]:checked');
    const hiddenInput = target.querySelector('input[type="hidden"]');
    if (!(hiddenInput instanceof HTMLInputElement)) return;

    hiddenInput.value = Array.from(checked, (input) => /** @type {HTMLInputElement} */ (input).value).join(', ');
  }

  #render = () => {
    const { participants, template } = this.refs;
    const form = document.getElementById(this.dataset.formId ?? '');
    const quantityInput = form instanceof HTMLFormElement ? form.elements.namedItem('quantity') : null;
    const quantity = Math.max(1, quantityInput instanceof HTMLInputElement ? parseInt(quantityInput.value) || 1 : 1);

    while (participants.children.length < quantity) {
      const number = String(participants.children.length + 1);
      participants.insertAdjacentHTML('beforeend', template.innerHTML.replaceAll('__i__', number));
    }

    while (participants.children.length > quantity) {
      participants.lastElementChild?.remove();
    }

    Array.from(participants.children).forEach((participant, index) => {
      const suffix = quantity > 1 ? ` ${index + 1}` : '';

      participant.querySelector('.course-form__participant-title')?.toggleAttribute('hidden', quantity === 1);

      for (const input of participant.querySelectorAll('input[data-key]')) {
        if (input instanceof HTMLInputElement) input.name = `properties[${input.dataset.key}${suffix}]`;
      }
    });
  };
}

customElements.define('course-form-component', CourseFormComponent);
